import type { FC, ReactNode } from 'react';
import { Typography } from 'antd';
import type { IconType } from 'react-icons';
import {
    TbBuildingFactory2,
    TbCalendarTime,
    TbDivide,
    TbEqual,
    TbHash,
    TbInfoCircle,
    TbMinus,
    TbParentheses,
    TbPlus,
    TbX,
} from 'react-icons/tb';
import { Link, useLocation } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTag from '@/shared/components/tags/SmallTag';
import EnrichedTooltip from '@/shared/components/tooltips/EnrichedTooltip';
import {
    BalanceSheetTabIcon,
    CashFlowsTabIcon,
    IncomeStatementTabIcon,
    MultiplesTabIcon,
    ReturnsTabIcon,
} from '@/shared/constants/financialTabIcons';

import type {
    FinancialFormula,
    FinancialFormulaCategory,
    FinancialFormulaOperandToken,
    FinancialFormulaOperatorToken,
} from '../../types';
import { createFinancialFormulaOperandHref } from './navigation';

interface FormulaCategoryPresentation {
    color: string;
    icon: IconType;
    label: string;
}

export interface FinancialMetricFormulaProps {
    formula: FinancialFormula;
    getOperandHref?: (operand: FinancialFormulaOperandToken) => string | undefined;
}

export interface FinancialMetricFormulaTooltipProps extends FinancialMetricFormulaProps {
    children?: ReactNode;
    description: string;
    label: string;
}

const getCategoryPresentation = (
    category: FinancialFormulaCategory,
    colors: Record<FinancialFormulaCategory, string>,
): FormulaCategoryPresentation => {
    const presentations: Record<FinancialFormulaCategory, Omit<FormulaCategoryPresentation, 'color'>> = {
        'balance-sheet': { icon: BalanceSheetTabIcon, label: 'Balance Sheet' },
        'cash-flow': { icon: CashFlowsTabIcon, label: 'Cash Flows' },
        constant: { icon: TbHash, label: 'Constant' },
        'income-statement': { icon: IncomeStatementTabIcon, label: 'Income Statement' },
        market: { icon: ReturnsTabIcon, label: 'Market Data' },
        operating: { icon: TbBuildingFactory2, label: 'Operating KPI' },
        time: { icon: TbCalendarTime, label: 'Time Period' },
        valuation: { icon: MultiplesTabIcon, label: 'Valuation' },
    };

    return { ...presentations[category], color: colors[category] };
};

const FORMULA_OPERATOR_ICONS: Record<FinancialFormulaOperatorToken['value'], IconType> = {
    '(': TbParentheses,
    ')': TbParentheses,
    '+': TbPlus,
    '−': TbMinus,
    '×': TbX,
    '÷': TbDivide,
    '=': TbEqual,
};

const FinancialFormulaOperatorIcon: FC<{
    color: string;
    value: FinancialFormulaOperatorToken['value'];
}> = ({ color, value }) => {
    const Icon = FORMULA_OPERATOR_ICONS[value];
    const isParenthesis = value === '(' || value === ')';

    return (
        <span
            aria-hidden='true'
            data-financial-formula-operator={value}
            style={{
                alignItems: 'center',
                color,
                display: 'inline-flex',
                flex: '0 0 auto',
                height: '1rem',
                justifyContent: isParenthesis ? 'flex-start' : 'center',
                lineHeight: 0,
                overflow: isParenthesis ? 'hidden' : undefined,
                verticalAlign: 'middle',
                width: isParenthesis ? '0.5rem' : '1rem',
            }}
        >
            <Icon
                focusable='false'
                size='1rem'
                style={{
                    display: 'block',
                    flex: '0 0 1rem',
                    minWidth: '1rem',
                    transform: value === ')' ? 'translateX(-0.5rem)' : undefined,
                }}
            />
        </span>
    );
};

export const FinancialMetricFormula: FC<FinancialMetricFormulaProps> = ({ formula, getOperandHref }) => {
    const { token } = useAntDesignTheme();
    const colors: Record<FinancialFormulaCategory, string> = {
        'balance-sheet': token.gold6,
        'cash-flow': token.green6,
        constant: token.geekblue6,
        'income-statement': token.blue6,
        market: token.cyan6,
        operating: token.volcano6,
        time: token.magenta6,
        valuation: token.purple6,
    };
    const accessibleFormula = formula.tokens
        .map((formulaToken) => (formulaToken.type === 'operand' ? formulaToken.label : formulaToken.value))
        .join(' ');

    return (
        <div
            aria-label={`Formula: ${accessibleFormula}`}
            data-financial-metric-formula='true'
            role={getOperandHref ? 'group' : 'img'}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                justifyContent: 'center',
                maxWidth: '100%',
            }}
        >
            {formula.tokens.map((formulaToken, index) => {
                if (formulaToken.type === 'operand') {
                    const presentation = getCategoryPresentation(formulaToken.category, colors);
                    const Icon = presentation.icon;
                    const href = getOperandHref?.(formulaToken);
                    const formulaTag = (
                        <SmallTag
                            accentColor={presentation.color}
                            bordered
                            icon={<Icon aria-hidden='true' size='0.875rem' />}
                            message={formulaToken.label}
                            padding='0.25rem 0.5rem'
                            styles={{
                                alignItems: 'center',
                                borderStyle: href ? 'dashed' : undefined,
                                cursor: href ? 'pointer' : undefined,
                                display: 'inline-flex',
                                fontWeight: 500,
                                gap: '0.3rem',
                                lineHeight: 1.2,
                                marginLeft: 0,
                                whiteSpace: 'nowrap',
                            }}
                        />
                    );

                    if (href) {
                        return (
                            <Link
                                aria-label={`Trace ${formulaToken.label} to its financial view`}
                                data-financial-formula-category={formulaToken.category}
                                key={`${formulaToken.key}-${index}`}
                                style={{ cursor: 'pointer', display: 'inline-flex', textDecoration: 'none' }}
                                to={href}
                            >
                                {formulaTag}
                            </Link>
                        );
                    }

                    return (
                        <span
                            aria-label={`${presentation.label}: ${formulaToken.label}`}
                            data-financial-formula-category={formulaToken.category}
                            key={`${formulaToken.key}-${index}`}
                        >
                            {formulaTag}
                        </span>
                    );
                }

                if (formulaToken.type === 'operator') {
                    return (
                        <FinancialFormulaOperatorIcon
                            color={token.colorTextTertiary}
                            key={`${formulaToken.type}-${formulaToken.value}-${index}`}
                            value={formulaToken.value}
                        />
                    );
                }

                return (
                    <Typography.Text
                        key={`${formulaToken.type}-${formulaToken.value}-${index}`}
                        style={{ color: token.colorTextSecondary }}
                    >
                        {formulaToken.value}
                    </Typography.Text>
                );
            })}
        </div>
    );
};

export const FinancialMetricFormulaTooltip: FC<FinancialMetricFormulaTooltipProps> = ({
    children,
    description,
    formula,
    label,
}) => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const getOperandHref = (operand: FinancialFormulaOperandToken) =>
        createFinancialFormulaOperandHref(location.pathname, location.search, operand);

    return (
        <EnrichedTooltip
            ariaLabel={`${label} definition`}
            description={description}
            interactiveVisual
            label={label}
            learnMoreTopic='financial metric methodology'
            placement='bottomLeft'
            visual={<FinancialMetricFormula formula={formula} getOperandHref={getOperandHref} />}
            width='28rem'
        >
            {children ?? (
                <TbInfoCircle
                    aria-hidden='true'
                    style={{ color: token.colorTextTertiary, flexShrink: 0, verticalAlign: '-0.1rem' }}
                />
            )}
        </EnrichedTooltip>
    );
};
